import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import getIsLoyaltyLeadRegion from 'loyalty/selectors/getIsLoyaltyLeadRegion'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatCurrency from 'components/Common/FormatCurrency'

function getTextColour(targetTier: App.LoyaltyTier, currentTier?: App.LoyaltyTier): `tier-${App.LoyaltyTier}-contrast` | undefined {
  if (targetTier === currentTier) {
    return `tier-${targetTier}-contrast`
  }
}

function getBenefit(tier: App.LoyaltyTier, benefitType: App.LoyaltyExtendedBenefitType, config: App.LoyaltyConfig) {
  return config.regionTierBenefits[tier].find(benefit => {
    if (benefitType === 'earn_points' || benefitType === 'use_points') return false
    return benefit.code === benefitType
  })
}

interface Props {
  benefitType: App.LoyaltyExtendedBenefitType
  config: App.LoyaltyConfig
  highlightTier: App.LoyaltyTier | undefined
  variant: React.ComponentProps<typeof BodyText>['variant']
}

function LoyaltyLandingPageBenefitsTableRowBenefit(props: Props) {
  const intl = useIntl()
  const { benefitType, config, highlightTier, variant } = props

  const isLeadLoyaltyRegion = useAppSelector(getIsLoyaltyLeadRegion)

  const unlimitedLabel = intl.formatMessage({
    id: 'ezlgsp9plBcd_xO8mM4u',
    defaultMessage: 'Unlimited',
    description: 'Label shown when a loyalty benefit has no usage limit',
  })

  const getBenefitValue = useCallback((benefit: App.LoyaltyRegionTierBenefit | undefined) => {
    if (benefitType === 'earn_points' || benefitType === 'use_points') {
      return <LineCheckIcon />
    }
    if (!benefit) return <NotAvailablePlaceholder variant="single" />
    switch (benefit.code) {
      case 'hotel_upgrade':
        return benefit.unlimited ? unlimitedLabel : benefit.limit
      case 'airport_transfer':
        return benefit.unlimited ? unlimitedLabel : benefit.limit
      case 'lounge_pass':
        return benefit.unlimited ? unlimitedLabel : benefit.limit
      case 'discounted_insurance':
        return intl.formatMessage(
          {
            id: 'hYs9FOW-dmvTtdSbO5M1',
            defaultMessage: '{amount}%',
            description: 'Percentage discount for discounted insurance loyalty benefit',
          },
          { amount: benefit.value?.amount ?? 0 },
        )
      case 'travelshoot_credit':
        if (isLeadLoyaltyRegion) return <FormatCurrency value={benefit.value?.amount ?? 0} format="dynamic" />
        return <LineCheckIcon />
      case 'tour_exp_credit':
        if (isLeadLoyaltyRegion) {
          return (
            <FormattedMessage
              id="9tD3M00EKR61rVzRj-PI"
              defaultMessage="{count}x {amount}"
              description="Loyalty benefit limit with currency amount in the loyalty section"
              values={{
                count: benefit.unlimited ? unlimitedLabel : benefit.limit,
                amount: <FormatCurrency value={benefit.value?.amount ?? 0} format="dynamic" />,
              }}
            />
          )
        }
        return <LineCheckIcon />
      case 'phone_support':
        return <LineCheckIcon />
      case 'esim_package':
        if (benefit?.value?.unit !== 'GB') return <NotAvailablePlaceholder variant="single" />
        return intl.formatMessage(
          {
            id: 'kbOjGIMq5fpuQI9S1nHJ',
            defaultMessage: '{amount} GB',
            description: 'eSIM data allowance per tier in benefits table (e.g. 1 GB, 3 GB)',
          },
          { amount: benefit.value.amount },
        )
      default:
        return <NotAvailablePlaceholder variant="single" />
    }
  }, [benefitType, isLeadLoyaltyRegion, unlimitedLabel, intl])

  const bronzeBenefit = useMemo(() => getBenefit('bronze', benefitType, config), [config, benefitType])
  const silverBenefit = useMemo(() => getBenefit('silver', benefitType, config), [config, benefitType])
  const goldBenefit = useMemo(() => getBenefit('gold', benefitType, config), [config, benefitType])
  const platinumBenefit = useMemo(() => getBenefit('platinum', benefitType, config), [config, benefitType])

  const bronzeCellContent = getBenefitValue(bronzeBenefit)

  return <>
    <BodyText variant={variant} weight="bold" align="center" colour={getTextColour('bronze', highlightTier)}>
      {bronzeCellContent}
    </BodyText>
    <BodyText variant={variant} weight="bold" align="center" colour={getTextColour('silver', highlightTier)}>
      {getBenefitValue(silverBenefit)}
    </BodyText>
    <BodyText variant={variant} weight="bold" align="center" colour={getTextColour('gold', highlightTier)}>
      {getBenefitValue(goldBenefit)}
    </BodyText>
    <BodyText variant={variant} weight="bold" align="center" colour={getTextColour('platinum', highlightTier)}>
      {getBenefitValue(platinumBenefit)}
    </BodyText>
  </>
}

export default LoyaltyLandingPageBenefitsTableRowBenefit
